import { ClientOrganizationCreateRequestTagsEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { TFunction } from 'i18next';
import {
  array,
  custom,
  email,
  enum as zodEnum,
  object,
  string,
  type ZodType
} from 'zod';

import type { ActiveExtensionData } from '@/features/profile-setting/components/organization-extensions/fragmentTypes';
import {
  getAllExtensions,
  validateExtensionData
} from '@/features/profile-setting/components/organization-extensions/registry';

export const AdminType = {
  SELF: 'SELF',
  OTHER: 'OTHER'
} as const;

export type AdminTypeValue = (typeof AdminType)[keyof typeof AdminType];

export type CreateOrganizationForm = {
  name: string;
  tags: ClientOrganizationCreateRequestTagsEnum[];
  geoAreaIds: string[];
  adminType: AdminTypeValue;
  adminEmail?: string;
  data: ActiveExtensionData;
};

type Props = {
  t: TFunction<'profile-setting', 'screens.organization-create'>;
};

export const createOrganizationSchema = ({ t }: Props) =>
  object({
    name: string().trim().min(1, t('errors.nameRequired')).max(255),
    tags: array(zodEnum(ClientOrganizationCreateRequestTagsEnum)).min(
      1,
      t('errors.tagsRequired')
    ),
    geoAreaIds: array(string()).min(1, t('errors.geoAreaRequired')),
    adminType: zodEnum(AdminType),
    adminEmail: string().trim().optional(),
    data: custom<ActiveExtensionData>()
  }).superRefine((data, ctx) => {
    validateExtensionData(data.data, getAllExtensions(), t, ctx, ['data']);
    if (data.adminType === AdminType.OTHER) {
      if (!data.adminEmail || data.adminEmail.trim().length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['adminEmail'],
          message: t('errors.adminEmailRequired')
        });
      } else {
        const emailResult = email().safeParse(data.adminEmail);
        if (!emailResult.success) {
          ctx.addIssue({
            code: 'custom',
            path: ['adminEmail'],
            message: t('errors.adminEmailInvalid')
          });
        }
      }
    }
  }) satisfies ZodType<CreateOrganizationForm>;
